import { Component, OnInit, Input, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { IonSpinner } from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { ReactiveFormsModule, ControlContainer, FormGroupDirective, FormGroup, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

import { environment } from '@app/environment';
import { MarvColorPrimary } from '@marv/types';
import { 
    MarvInputComponent, MarvInputDateComponent, MarvSelectItem, MarvSectionTitleComponent,
    MarvCheckboxComponent 
} from '@marv/components';
import { 
    RetailerSelectComponent, AreaSelectComponent, SectorSelectComponent, TypeMissionSelectComponent,
    OperationSelectComponent, PermanentAgentSelectComponent,
    StoreSelectComponent, PhotoFiltersValues
} from '@app/components';
import { OperationCard, InstantPhotoFilterTag } from '@app/models';
import { PhotoService, StoreService, CustomerSectorService } from '@app/services';

@Component({
    selector: 'app-photo-gallery-filters-all',
    templateUrl: './all.component.html',
    styleUrls: ['./all.component.scss'],
    imports: [
        IonSpinner, TranslatePipe, 
        forwardRef(() => MarvInputComponent), forwardRef(() => MarvInputDateComponent), forwardRef(() => MarvSectionTitleComponent), 
        forwardRef(() => MarvCheckboxComponent), forwardRef(() => RetailerSelectComponent), forwardRef(() => AreaSelectComponent), 
        forwardRef(() => SectorSelectComponent), forwardRef(() => TypeMissionSelectComponent), forwardRef(() => OperationSelectComponent), 
        forwardRef(() => PermanentAgentSelectComponent), forwardRef(() => StoreSelectComponent)
    ],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ]
})
export class PhotoGalleryFiltersAllComponent  implements OnInit {

    @Input({alias : 'operationsAll', required : true}) operationsAll: Array<OperationCard> = [];
    @Input({alias : 'photoFiltersValues', required : true}) photoFiltersValues: PhotoFiltersValues;
    @Input({alias : 'hideFiltersStore', required : false}) hideFiltersStore: boolean = false;
    
    @ViewChild('retailerSelect') retailerSelect : RetailerSelectComponent;
    @ViewChild('storeSelect') storeSelect : StoreSelectComponent;
    
    isFetchingInstantPhotoFilterTags: boolean = false;
    startDate: Date;
    endDate: Date;
    instantPhotoFilterTags: Array<InstantPhotoFilterTag> = [];
    stores: Array<MarvSelectItem> = [];
    sectors: Array<MarvSelectItem> = [];
    
    instantFiltersCountSubscription: Subscription;
    dateStartSubscription: Subscription;
    endDateSubscription: Subscription;
    operationsSubscription: Subscription;
    updateAreasSubscription: Subscription;
    updateRetailersSubscription: Subscription;
    
    constructor(
        public formGroupDirective: FormGroupDirective,
        private photoService: PhotoService
    ) { }

    ngOnInit() {
        
        this.dateStartSubscription = this.formGroupDirective.form.controls['startDate'].valueChanges.subscribe(newValue => {
            this.getPeriodDates();
        });
        
        this.endDateSubscription = this.formGroupDirective.form.controls['endDate'].valueChanges.subscribe(newValue => {
           this.getPeriodDates();
        });
        
        this.operationsSubscription = this.formGroupDirective.form.controls['operationsIds'].valueChanges.subscribe(async (newValue) => {
            await this.loadInstantPhotoFilterTags();
        });
        
        this.filterStores();
        this.updateRetailersSubscription = this.formGroupDirective.form.controls['retailersCodes'].valueChanges.subscribe(value => {
            this.filterStores();
        }); 
        
        this.filterSectors();        
        this.updateAreasSubscription = this.formGroupDirective.form.controls['areasIds'].valueChanges.subscribe(value => {
            this.filterSectors();
        });
    }
    
    ngOnDestroy() {
        if(this.instantFiltersCountSubscription){
            this.instantFiltersCountSubscription.unsubscribe();
        } 
        if(this.dateStartSubscription){
            this.dateStartSubscription.unsubscribe();
        } 
        if(this.endDateSubscription){
            this.endDateSubscription.unsubscribe();
        } 
        if(this.operationsSubscription){
            this.operationsSubscription.unsubscribe();
        } 
    }
    
    async ngOnChanges(changes: SimpleChanges) {        
        if(changes['operationsAll'] && changes['operationsAll'].currentValue){     
            
            let operationsIds: Array<number> = [];
            let permanentsOperationsIds: Array<number> = [];
            
            this.operationsAll.filter(o => o.type === 'loga').forEach(o => {
                operationsIds.push(o.id);
            });
            
            this.operationsAll.filter(o => o.type === 'lgg').forEach(o => {
                permanentsOperationsIds.push(o.id);
            });
                        
            await this.loadInstantPhotoFilterTags();
                 
        }
        
        if(changes['photoFiltersValues'] && changes['photoFiltersValues'].currentValue){
            
            this.startDate = this.photoFiltersValues.startDate;
            this.endDate = this.photoFiltersValues.endDate;
            
            if(this.instantFiltersCountSubscription){
                this.instantFiltersCountSubscription.unsubscribe();
            } 
            if(typeof this.photoFiltersValues.instantFiltersCountSubject.subscribe === 'function'){
                this.instantFiltersCountSubscription = this.photoFiltersValues.instantFiltersCountSubject.subscribe(newValue => {
                    this.updateInstantPhotoFilterTagsCount();
                });
            }
                        
        }
    }
    
    async loadInstantPhotoFilterTags(){
        
        this.isFetchingInstantPhotoFilterTags = true;
        
        let operationsIds: Array<number> = [];          
        if(this.formGroupDirective.form.controls['operationsIds'].value?.length > 0){
            this.formGroupDirective.form.controls['operationsIds'].value.forEach((o: string) => {
                let opInfos = o.split('|');
                if(opInfos.length === 2 && opInfos[0] === 'loga'){
                    operationsIds.push(parseInt(opInfos[1]));
                }
            });
        }
        else{
            this.operationsAll.forEach(o => {
                if(o.type === 'loga'){
                    operationsIds.push(o.id);
                }            
            });
        }
        
        let nbInstantPhotoFilterTags = this.instantPhotoFilterTags.length;
        this.instantPhotoFilterTags = [];            
        for(let index=0;index<nbInstantPhotoFilterTags;index++){
            this.formGroupDirective.form.removeControl('instantPhotoFilterTag_'+index, {emitEvent: false});
        }                

        if(operationsIds.length > 0){    
            let instantPhotoFilterTags = await this.photoService.loadInstantPhotoFilterTags(operationsIds);   
            instantPhotoFilterTags.forEach((value, index) => {
                this.formGroupDirective.form.addControl('instantPhotoFilterTag_'+index, new FormControl(false), {emitEvent: false});   
            });                            
            this.instantPhotoFilterTags = instantPhotoFilterTags;
        }
        
        this.isFetchingInstantPhotoFilterTags = false;
    }
    
    async updateInstantPhotoFilterTagsCount(){       
        this.photoFiltersValues.instantFiltersCount.forEach((count, normalizedTag) => {
            let tag = this.instantPhotoFilterTags.find(i => i.normalizedTag === normalizedTag);
            if(tag){
                tag.counter = count;
            }
        });
    }

    getPeriodDates(){             
        this.startDate = this.formGroupDirective.form.controls['startDate'].value;          
        this.endDate = this.formGroupDirective.form.controls['endDate'].value;    
    }
    
    onChangeOperationsItems(operationsItems: Array<MarvSelectItem>){        
        let tabIds = this.formGroupDirective.form.controls['operationsIds'].value || [];
        let tabIdsItems = operationsItems.map(i => i.value);
        tabIds = tabIds.filter((i: number) => tabIdsItems.indexOf(i) !== -1);
        this.formGroupDirective.form.controls['operationsIds'].setValue(tabIds);
    }
    
    filterStores(){
        let stores: Array<MarvSelectItem> = [];
        let retailersCodes = this.formGroupDirective.form.controls['retailersCodes'].value;
        let storesCodes = this.formGroupDirective.form.controls['storesCodes'].value;
        for(let s of StoreService.Stores){
            if((!retailersCodes || retailersCodes.length === 0) || retailersCodes.indexOf(s.retailer.code) !== -1){
                stores.push(new MarvSelectItem(s.name, s.code, environment.URL_LOGO_RETAILER.replace('#retailer_name#', s.retailer.name)));
            }
            else{
                let indexSector = storesCodes.indexOf(s.code)
                if(indexSector !== -1){
                    storesCodes.splice(indexSector, 1);
                }
            }
        }   
        this.formGroupDirective.form.controls['storesCodes'].setValue(storesCodes, {emitEvent: false}); 
        this.stores = stores;
    }
    
    filterSectors(){        
        let sectors: Array<MarvSelectItem> = [];
        let areasIds = this.formGroupDirective.form.controls['areasIds'].value;
        let sectorsIds = this.formGroupDirective.form.controls['sectorsIds'].value;
        for(let s of CustomerSectorService.Sectors){
            if((!areasIds || areasIds.length === 0) || areasIds.indexOf(s.area?.id) !== -1){
                sectors.push(new MarvSelectItem(s.name, s.id));
            }
            else{
                let indexSector = sectorsIds.indexOf(s.id)
                if(indexSector !== -1){
                    sectorsIds.splice(indexSector, 1);
                }
            }
        }   
        this.formGroupDirective.form.controls['sectorsIds'].setValue(sectorsIds, {emitEvent: false});     
        this.sectors = sectors;
    }
}
